<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Edit Recharged Benefit Rewards
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="isLoadingData"
            type="info"
            text
            dense
          >
            Loading rewards...
          </v-alert>

          <div v-else>
            <!-- Common Fields -->
            <v-row>
              <v-col sm="6">
                <v-text-field
                  v-model="commonFields.title"
                  outlined
                  label="Offer Name"
                  prepend-inner-icon="mdi-tag"
                  :error-messages="errors.title"
                ></v-text-field>
              </v-col>

              <v-col sm="6">
                <v-text-field
                  v-model="commonFields.recharge_amount"
                  outlined
                  label="Recharge Amount"
                  prepend-inner-icon="mdi-currency-inr"
                  type="number"
                  min="0"
                  step="0.01"
                  :error-messages="errors.recharge_amount"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Rewards Section -->
            <div class="d-flex align-center mb-4">
              <h3 class="text-h6">Rewards</h3>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                small
                rounded
                @click="addReward"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Reward
              </v-btn>
            </div>

            <!-- Reward Cards -->
            <v-row>
              <v-col
                v-for="(reward, index) in rewards"
                :key="reward.id || index"
                cols="12"
              >
                <v-card outlined class="pa-4 mb-3">
                  <div class="d-flex align-center mb-3">
                    <h4 class="text-subtitle-1">
                      Reward #{{ index + 1 }}
                      <v-chip x-small color="primary" v-if="reward.id">Existing</v-chip>
                      <v-chip x-small color="success" v-else>New</v-chip>
                    </h4>
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="removeReward(index)"
                      v-if="rewards.length > 1"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <v-row>
                    <!-- Reward Type -->
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="reward.reward_type"
                        outlined
                        dense
                        :items="rewardTypeItems"
                        item-text="text"
                        item-value="value"
                        label="Reward Type"
                        prepend-inner-icon="mdi-gift"
                        @change="onRewardTypeChange(index)"
                      ></v-autocomplete>
                    </v-col>

                    <!-- Coin Amount (conditional) -->
                    <v-col sm="4" v-if="reward.reward_type === 'coin'">
                      <v-text-field
                        v-model="reward.coin_amount"
                        outlined
                        dense
                        label="Coins Earned"
                        prepend-inner-icon="mdi-bitcoin"
                        type="number"
                        min="0"
                        step="0.01"
                      ></v-text-field>
                    </v-col>

                    <!-- Store Item (conditional) -->
                    <v-col sm="4" v-if="reward.reward_type === 'store_item'">
                      <v-autocomplete
                        v-model="reward.store_item_id"
                        outlined
                        dense
                        :items="storeItems"
                        item-text="text"
                        item-value="value"
                        label="Store Item"
                        prepend-inner-icon="mdi-store"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Duration (conditional) -->
                    <v-col sm="4" v-if="reward.reward_type === 'store_item'">
                      <v-text-field
                        v-model="reward.duration"
                        outlined
                        dense
                        label="Duration (Days)"
                        prepend-inner-icon="mdi-calendar-clock"
                        type="number"
                        min="0"
                        hint="0 for Permanent"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <!-- Status -->
            <v-row>
              <v-col sm="12">
                <v-switch
                  v-model="commonFields.is_active"
                  inset
                  color="primary"
                  :label="`Status: ${commonFields.is_active ? 'Active' : 'Inactive'}`"
                ></v-switch>
              </v-col>
            </v-row>

            <v-divider class="mt-5 mb-5"></v-divider>

            <v-btn
              :loading="isLoading"
              :disabled="isLoading || rewards.length === 0"
              color="primary"
              rounded
              dark
              @click="save"
            >
              Update All Rewards ({{ rewards.length }})
            </v-btn>
            <!-- <v-btn
              :loading="isDeleting"
              :disabled="isDeleting"
              color="error"
              rounded
              dark
              class="ml-3"
              @click="deleteAllRewards"
            >
              Delete All Rewards
            </v-btn> -->
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="600">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon
                large
                style="color: red; font-size: 90px; width: 200px !important"
              >
                mdi-delete-alert
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 style="color: red">Confirm Delete</h1>
                    <br />
                    <h3>Are you sure you want to delete this recharged benefit?</h3>
                    <br />
                    <v-btn color="error" @click="confirmDelete" class="mr-3">
                      Yes, Delete
                    </v-btn>
                    <v-btn color="secondary" @click="deleteDialog = false">
                      Cancel
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Delete Success Dialog -->
    <v-dialog v-model="deleteSuccessDialog" width="600">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon
                large
                style="color: green; font-size: 90px; width: 200px !important"
              >
                mdi-check-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <v-row>
                  <v-col>
                    <h1 style="color: green">Success!</h1>
                    <br />
                    <h3>Recharged Benefit deleted successfully.</h3>
                    <br />
                    <v-btn color="success" @click="goToIndex">
                      OK
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "RechargedBenefitEditPage",
  data() {
    return {
      commonFields: {
        title: "",
        recharge_amount: 0,
        is_active: true,
      },
      rewards: [],
      isLoading: false,
      isLoadingData: true,
      isDeleting: false,
      storeItems: [],
      rewardTypeItems: [
        { id: 1, text: "Coin", value: "coin" },
        { id: 2, text: "Store Item", value: "store_item" },
      ],
      deleteDialog: false,
      deleteSuccessDialog: false,
      errors: {},
      initialData: null, // Store initial reward data
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=reward_category`)
        .then((masters) => {
          masters = masters.data;
          //Store Items
          if (masters.storeItem) {
            masters.storeItem.forEach((storeItem) => {
              this.storeItems.push({
                id: storeItem.id,
                text: storeItem.name,
                value: storeItem.id,
              });
            });
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    async getData() {
      this.isLoadingData = true;
      let encryptedId = this.$route.params.id;

      try {
        // Get the single reward first
        const response = await axios.get(`/recharged_benefits/${encryptedId}`);
        const singleReward = response.data.data;

        // Store initial data
        this.initialData = { ...singleReward };

        // Set common fields
        this.commonFields = {
          title: singleReward.title,
          recharge_amount: singleReward.recharge_amount,
          is_active: singleReward.is_active,
        };

        // Now fetch all rewards with same title and recharge_amount
        const allRewardsResponse = await axios.get('/recharged_benefits', {
          params: {
            search_keyword: singleReward.title,
          }
        });

        // Filter rewards with exact match on title and recharge_amount
        const matchingRewards = allRewardsResponse.data.data.filter(r =>
          r.title === singleReward.title &&
          parseFloat(r.recharge_amount) === parseFloat(singleReward.recharge_amount)
        );

        // Load all matching rewards
        this.rewards = matchingRewards.map(reward => ({
          id: reward.id,
          reward_type: reward.reward_type,
          coin_amount: reward.coin_amount,
          store_item_id: reward.store_item_id,
          duration: reward.duration,
        }));

        console.log("Loaded rewards:", this.rewards);
      } catch (error) {
        console.error("Error fetching data:", error);
      }

      this.isLoadingData = false;
    },

    addReward() {
      this.rewards.push({
        reward_type: "",
        coin_amount: null,
        store_item_id: null,
        duration: 0,
      });
    },

    removeReward(index) {
      // Simply remove from array - backend will handle deletion automatically
      this.rewards.splice(index, 1);
    },

    onRewardTypeChange(index) {
      // Reset fields based on reward type
      const reward = this.rewards[index];
      if (reward.reward_type === 'coin') {
        reward.store_item_id = null;
        reward.duration = 0;
      } else if (reward.reward_type === 'store_item') {
        reward.coin_amount = null;
      }
    },

    async save() {
      try {
        this.isLoading = true;

        // Get the first reward ID for the main update
        const mainRewardId = this.initialData?.id || this.rewards.find(r => r.id)?.id;

        // Prepare payload as per backend requirements (PJP pattern)
        const payload = {
          id: mainRewardId, // Main reward ID for update operation
          title: this.commonFields.title,
          recharge_amount: this.commonFields.recharge_amount,
          rewards: this.rewards.map(reward => ({
            id: reward.id, // Include ID for existing rewards (update) or null for new (create)
            title: this.commonFields.title,
            reward_type: reward.reward_type,
            coin_amount: reward.coin_amount,
            store_item_id: reward.store_item_id,
            recharge_amount: this.commonFields.recharge_amount,
            duration: reward.duration,
            is_active: this.commonFields.is_active,
          }))
        };

        // Send single request with all rewards
        // Backend will handle: create new, update existing, delete missing
        const response = await axios.post("/recharged_benefits", payload);

        this.isLoading = false;

        if (response.data.success) {
          this.$router.push("/rechargedbenefits");
        }
      } catch (e) {
        this.isLoading = false;
        console.log(e);
        if (e.response && e.response.data) {
          this.errors = e.response.data.errors || {};
        }
      }
    },

    deleteAllRewards() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        this.isDeleting = true;
        this.deleteDialog = false;

        // Delete all rewards in the group
        const deletePromises = this.rewards
          .filter(r => r.id)
          .map(reward => axios.post(`/recharged_benefits/delete/${reward.id}`));

        await Promise.all(deletePromises);

        this.isDeleting = false;
        this.deleteSuccessDialog = true;
      } catch (error) {
        this.isDeleting = false;
        console.error("Error deleting recharged benefits:", error);
      }
    },

    goToIndex() {
      this.deleteSuccessDialog = false;
      this.$router.push("/rechargedbenefits");
    },
  },
};
</script>